import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, Heart, Lock, Check, Building, Copy, CheckCircle, ExternalLink, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const predefinedAmounts = [10, 25, 50, 100, 250];

const DonateModal = ({ post, onClose }) => {
  const { isAuthenticated, token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [bankInfo, setBankInfo] = useState(null);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [platformPaypal, setPlatformPaypal] = useState('paypal.me/moneyfor');

  useEffect(() => {
    // Fetch platform PayPal address
    axios.get(`${API_URL}/api/platform/paypal`).then(res => {
      if (res.data?.paypal_address) setPlatformPaypal(res.data.paypal_address);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (paymentMethod === 'bank_transfer' && isAuthenticated) {
      fetchBankInfo();
    }
  }, [paymentMethod, isAuthenticated]);

  const fetchBankInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts/${post.post_id}/bank-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankInfo(response.data);
    } catch { /* ignore */ }
  };

  const getFinalAmount = () => {
    return isCustom ? parseFloat(customAmount) || 0 : selectedAmount;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(language === 'it' ? 'Copiato!' : 'Copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDonate = async () => {
    if (!isAuthenticated) {
      onClose();
      navigate('/login');
      return;
    }

    const amount = getFinalAmount();
    if (amount < 1) {
      toast.error((language === 'it' ? 'Importo minimo: 1 ' : 'Minimum amount: 1 ') + post.currency);
      return;
    }

    setLoading(true);
    try {
      if (paymentMethod === 'stripe') {
        const response = await axios.post(
          `${API_URL}/api/payments/stripe/checkout`,
          {
            post_id: post.post_id,
            amount: amount,
            currency: post.currency,
            origin_url: window.location.origin,
            message: message || null,
            anonymous: anonymous
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = response.data.url;
      } else if (paymentMethod === 'bank_transfer') {
        setShowBankDetails(true);
        setLoading(false);
      } else if (paymentMethod === 'paypal') {
        const paypalAddr = platformPaypal.replace(/^https?:\/\//, '').replace(/^www\./, '');
        const paypalUrl = `https://paypal.me/${paypalAddr.replace('paypal.me/', '')}/${amount}`;
        window.open(paypalUrl, '_blank');
        toast.info(language === 'it' 
          ? 'Si aprirà PayPal in una nuova finestra.' 
          : 'PayPal will open in a new window.');
        setLoading(false);
      } else if (paymentMethod === 'wise') {
        window.open('https://wise.com/send', '_blank');
        toast.info(language === 'it' ? 'Si aprirà Wise in una nuova finestra.' : 'Wise will open in a new window.');
        setLoading(false);
      } else if (paymentMethod === 'mpesa') {
        toast.info(language === 'it' 
          ? 'Invia tramite M-Pesa al numero indicato nella richiesta, oppure contatta il richiedente via chat.' 
          : 'Send via M-Pesa to the number indicated in the request, or contact the requester via chat.');
        setLoading(false);
      } else if (paymentMethod === 'western_union') {
        window.open('https://www.westernunion.com/send-money', '_blank');
        toast.info(language === 'it' ? 'Si aprirà Western Union in una nuova finestra.' : 'Western Union will open in a new window.');
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'it' ? 'Errore nel processamento' : 'Processing error'));
      setLoading(false);
    }
  };

  // Show bank transfer details
  if (showBankDetails && bankInfo) {
    return (
      <div className="space-y-6" data-testid="bank-transfer-details">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Building className="h-8 w-8 text-emerald-600" />
          </div>
          <h3 className="font-heading text-xl font-semibold text-stone-900">
            {language === 'it' ? 'Effettua il Bonifico' : 'Make the Bank Transfer'}
          </h3>
          <p className="text-stone-600 mt-2 text-sm">
            {language === 'it' 
              ? 'Usa questi dati per effettuare il bonifico bancario'
              : 'Use these details to make the bank transfer'}
          </p>
        </div>

        {bankInfo.has_bank_info ? (
          <div className="space-y-4">
            <div className="bg-stone-50 rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-stone-500">IBAN</span>
                <button onClick={() => copyToClipboard(bankInfo.iban)} className="text-emerald-600 hover:text-emerald-700">
                  {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
              <p className="font-mono font-semibold text-stone-900">{bankInfo.iban}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-stone-50 rounded-xl p-4">
                <span className="text-sm text-stone-500 block mb-1">
                  {language === 'it' ? 'Intestatario' : 'Account Holder'}
                </span>
                <p className="font-semibold text-stone-900">{bankInfo.account_holder}</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-4">
                <span className="text-sm text-stone-500 block mb-1">
                  {language === 'it' ? 'Banca' : 'Bank'}
                </span>
                <p className="font-semibold text-stone-900">{bankInfo.bank_name || '-'}</p>
              </div>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <span className="text-sm text-amber-800 block mb-1">
                {language === 'it' ? 'Causale (importante!)' : 'Reference (important!)'}
              </span>
              <div className="flex justify-between items-center">
                <p className="font-mono font-bold text-amber-900">{bankInfo.reference}</p>
                <button onClick={() => copyToClipboard(bankInfo.reference)} className="text-amber-600 hover:text-amber-700">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4">
              <span className="text-sm text-emerald-800 block mb-1">
                {language === 'it' ? 'Importo da trasferire' : 'Amount to transfer'}
              </span>
              <p className="font-heading text-2xl font-bold text-emerald-800">
                {getFinalAmount().toFixed(2)} {post.currency}
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 bg-amber-50 rounded-xl">
            <p className="text-amber-800 text-sm">
              {language === 'it' 
                ? 'Il destinatario non ha ancora configurato i dati bancari. Contattalo via chat.'
                : 'The recipient has not yet configured bank details. Contact them via chat.'}
            </p>
          </div>
        )}

        <Button onClick={() => { setShowBankDetails(false); onClose(); }}
          className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold"
        >
          {language === 'it' ? 'Ho capito' : 'Got it'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1" data-testid="donate-modal">
      {/* Amount Selection */}
      <div>
        <Label className="text-base font-semibold">
          {language === 'it' ? 'Seleziona importo' : 'Select amount'}
        </Label>
        <div className="grid grid-cols-5 gap-2 mt-3">
          {predefinedAmounts.map((amount) => (
            <button key={amount} type="button"
              onClick={() => { setSelectedAmount(amount); setIsCustom(false); }}
              className={`py-3 rounded-xl font-semibold transition-all text-sm ${
                selectedAmount === amount && !isCustom
                  ? 'bg-emerald-800 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
              }`}
            >
              {amount}€
            </button>
          ))}
        </div>
        <div className="mt-3">
          <Label className="text-sm text-stone-600">
            {language === 'it' ? 'Importo personalizzato' : 'Custom amount'}
          </Label>
          <div className="relative mt-1">
            <Input data-testid="custom-amount-input" type="number" min="1" step="0.01" placeholder="0.00"
              value={customAmount}
              onChange={(e) => { setCustomAmount(e.target.value); setIsCustom(true); }}
              className="h-12 rounded-xl pr-12"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-500">{post.currency}</span>
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div>
        <Label className="text-base font-semibold">
          {language === 'it' ? 'Metodo di pagamento' : 'Payment method'}
        </Label>
        <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="mt-3 space-y-2">
          <div className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
            paymentMethod === 'stripe' ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'
          }`} onClick={() => setPaymentMethod('stripe')}>
            <RadioGroupItem value="stripe" id="stripe" />
            <CreditCard className="h-5 w-5 text-stone-600" />
            <Label htmlFor="stripe" className="cursor-pointer flex-1">
              <span className="font-medium">{language === 'it' ? 'Carta di credito/debito' : 'Credit/debit card'}</span>
              <span className="text-xs text-stone-500 block">Visa, Mastercard, Amex</span>
            </Label>
          </div>
          
          <div className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
            paymentMethod === 'bank_transfer' ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'
          }`} onClick={() => setPaymentMethod('bank_transfer')}>
            <RadioGroupItem value="bank_transfer" id="bank_transfer" />
            <Building className="h-5 w-5 text-stone-600" />
            <Label htmlFor="bank_transfer" className="cursor-pointer flex-1">
              <span className="font-medium">{language === 'it' ? 'Bonifico Bancario' : 'Bank Transfer'}</span>
              <span className="text-xs text-stone-500 block">IBAN / SEPA</span>
            </Label>
          </div>

          <div className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
            paymentMethod === 'paypal' ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'
          }`} onClick={() => setPaymentMethod('paypal')}>
            <RadioGroupItem value="paypal" id="paypal" />
            <div className="h-5 w-5 flex items-center justify-center">
              <span className="text-[#003087] font-bold text-sm">P</span>
            </div>
            <Label htmlFor="paypal" className="cursor-pointer flex-1">
              <span className="font-medium">PayPal</span>
              <span className="text-xs text-stone-500 block flex items-center gap-1">
                {language === 'it' ? 'Si aprirà PayPal.me' : 'Opens PayPal.me'}
                <ExternalLink className="h-3 w-3" />
              </span>
            </Label>
          </div>

          {/* International Methods */}
          <div className="pt-2 pb-1">
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider flex items-center gap-1.5">
              <Globe className="h-3.5 w-3.5" />
              {language === 'it' ? 'Internazionali' : 'International'}
            </p>
          </div>

          <div className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
            paymentMethod === 'wise' ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'
          }`} onClick={() => setPaymentMethod('wise')}>
            <RadioGroupItem value="wise" id="wise" />
            <div className="h-5 w-5 flex items-center justify-center">
              <span className="text-[#9FE870] font-bold text-sm">W</span>
            </div>
            <Label htmlFor="wise" className="cursor-pointer flex-1">
              <span className="font-medium">Wise</span>
              <span className="text-xs text-stone-500 block">
                {language === 'it' ? 'Trasferimenti internazionali economici' : 'Low-cost international transfers'}
              </span>
            </Label>
          </div>

          <div className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
            paymentMethod === 'mpesa' ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'
          }`} onClick={() => setPaymentMethod('mpesa')}>
            <RadioGroupItem value="mpesa" id="mpesa" />
            <div className="h-5 w-5 flex items-center justify-center">
              <span className="text-[#4CAF50] font-bold text-sm">M</span>
            </div>
            <Label htmlFor="mpesa" className="cursor-pointer flex-1">
              <span className="font-medium">M-Pesa</span>
              <span className="text-xs text-stone-500 block">
                {language === 'it' ? 'Pagamenti mobile Africa/Asia' : 'Mobile payments Africa/Asia'}
              </span>
            </Label>
          </div>

          <div className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
            paymentMethod === 'western_union' ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'
          }`} onClick={() => setPaymentMethod('western_union')}>
            <RadioGroupItem value="western_union" id="western_union" />
            <div className="h-5 w-5 flex items-center justify-center">
              <span className="text-[#FFDD00] font-bold text-sm">WU</span>
            </div>
            <Label htmlFor="western_union" className="cursor-pointer flex-1">
              <span className="font-medium">Western Union</span>
              <span className="text-xs text-stone-500 block">
                {language === 'it' ? 'Invio denaro globale, anche senza banca' : 'Global money transfer, even without a bank'}
              </span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Message */}
      <div>
        <Label htmlFor="message">
          {language === 'it' ? 'Messaggio (opzionale)' : 'Message (optional)'}
        </Label>
        <Textarea id="message" data-testid="donation-message-input"
          placeholder={language === 'it' ? 'Scrivi un messaggio di supporto...' : 'Write a support message...'}
          className="mt-2 rounded-xl resize-none" rows={2}
          value={message} onChange={(e) => setMessage(e.target.value)}
        />
      </div>

      {/* Anonymous */}
      <div className="flex items-center space-x-3">
        <Checkbox id="anonymous" data-testid="anonymous-checkbox" checked={anonymous} onCheckedChange={setAnonymous} />
        <Label htmlFor="anonymous" className="cursor-pointer">
          {language === 'it' ? 'Donazione anonima' : 'Anonymous donation'}
        </Label>
      </div>

      {/* Summary */}
      <div className="bg-stone-50 rounded-xl p-4">
        <div className="flex justify-between items-center">
          <span className="text-stone-600">{language === 'it' ? 'Totale donazione' : 'Total donation'}</span>
          <span className="font-heading text-2xl font-bold text-emerald-800">
            {getFinalAmount().toFixed(2)} {post.currency}
          </span>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="flex items-center justify-center gap-4 text-xs text-stone-500">
        <div className="flex items-center gap-1"><Lock className="h-3 w-3" /><span>{language === 'it' ? 'Pagamento sicuro' : 'Secure payment'}</span></div>
        <div className="flex items-center gap-1"><Check className="h-3 w-3" /><span>{language === 'it' ? 'Crittografia SSL' : 'SSL encryption'}</span></div>
      </div>

      {/* Submit Button */}
      <Button data-testid="complete-donation-btn" onClick={handleDonate}
        disabled={loading || getFinalAmount() < 1}
        className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-lg font-bold uppercase tracking-wide shadow-lg shadow-amber-500/30"
      >
        {loading ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
        ) : (
          <><Heart className="h-5 w-5 mr-2" fill="currentColor" />{language === 'it' ? 'Completa donazione' : 'Complete donation'}</>
        )}
      </Button>
    </div>
  );
};

export default DonateModal;
